// ============================================
// アビリティストック・ボタン表示（右パネル）
// ============================================

import { getP5 } from '../../core/p5-instance';
import {
    getSelectedShip,
    getBombStock,
    getHomingStock,
    getHomingActive,
    getCannonStock,
    getCannonCharging,
    getCannonActive
} from '../../store/index';
import { getDiffConfig, getAbilityButtonBounds } from '../../utils/helpers';

/**
 * アビリティボタン表示（右パネル下部）- MIX-1デザイン
 */
export function drawAbilityButton(): void {
    const selectedShip = getSelectedShip();
    const { x, y, size, height } = getAbilityButtonBounds();
    const diffCfg = getDiffConfig();

    if (selectedShip === 'bomber') {
        const bombStock = getBombStock();
        drawMix1Button(x, y, size, height, bombStock, diffCfg.maxBombs,
            [0, 240, 255], 'bomb', 'BOMB', false);
    } else if (selectedShip === 'hunter') {
        const homingStock = getHomingStock();
        const homingActive = getHomingActive();
        drawMix1Button(x, y, size, height, homingStock, diffCfg.maxHoming,
            [255, 136, 0], 'homing', 'HOMING', homingActive);
    } else if (selectedShip === 'lancer') {
        const cannonStock = getCannonStock();
        const cannonCharging = getCannonCharging();
        const cannonActive = getCannonActive();
        drawMix1Button(x, y, size, height, cannonStock, diffCfg.maxCannon,
            [200, 50, 255], 'cannon', 'CANNON', cannonActive || cannonCharging);
    }
}

/**
 * MIX-1デザインのボタンを描画
 * 縦カード＋上部アクセントライン＋横並びドットゲージ
 */
function drawMix1Button(
    x: number,
    y: number,
    width: number,
    height: number,
    stock: number,
    maxStock: number,
    color: readonly [number, number, number],
    icon: 'bomb' | 'homing' | 'cannon',
    label: string,
    isActive: boolean
): void {
    const p = getP5();
    const halfW = width / 2;
    const halfH = height / 2;
    const cornerRadius = 10;

    // 背景（グラデーション + 枠線）
    const borderAlpha = stock > 0 ? 0.3 : 0.15;
    const ctx = p.drawingContext as CanvasRenderingContext2D;

    // グラデーション背景（Canvas 2D API）
    ctx.save();
    const bgGradient = ctx.createLinearGradient(x, y - halfH, x, y + halfH);
    if (stock > 0) {
        bgGradient.addColorStop(0, `rgba(${color[0]}, ${color[1]}, ${color[2]}, 0.15)`);
        bgGradient.addColorStop(1, `rgba(${color[0]}, ${color[1]}, ${color[2]}, 0.05)`);
    } else {
        bgGradient.addColorStop(0, `rgba(${color[0]}, ${color[1]}, ${color[2]}, 0.05)`);
        bgGradient.addColorStop(1, `rgba(${color[0]}, ${color[1]}, ${color[2]}, 0.02)`);
    }
    ctx.fillStyle = bgGradient;
    ctx.beginPath();
    ctx.roundRect(x - halfW, y - halfH, width, height, cornerRadius);
    ctx.fill();
    ctx.restore();

    // 枠線
    p.noFill();
    p.stroke(color[0], color[1], color[2], borderAlpha * 255);
    p.strokeWeight(1);
    p.rect(x - halfW, y - halfH, width, height, cornerRadius);

    // 上部アクセントライン（クリッピング + shadowBlurでグロー効果）
    const accentHeight = 3;
    if (stock > 0) {
        ctx.save();

        // ボタン形状でクリッピング
        ctx.beginPath();
        ctx.roundRect(x - halfW, y - halfH, width, height, cornerRadius);
        ctx.clip();

        const pulse = isActive ? 0.8 + p.sin(p.frameCount * 0.2) * 0.2 : 1.0;

        // グロー効果（Canvas 2D shadowBlur）
        ctx.shadowColor = `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${pulse})`;
        ctx.shadowBlur = 18;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;

        // アクセントライン本体（shadowBlurでグローが付く）
        ctx.fillStyle = `rgb(${color[0]}, ${color[1]}, ${color[2]})`;
        ctx.fillRect(x - halfW, y - halfH, width, accentHeight);

        // シャドウをリセット
        ctx.shadowBlur = 0;

        ctx.restore();
    }

    // アイコン描画
    const iconY = y - 18;
    drawButtonIcon(p, x, iconY, icon, color, stock > 0, isActive);

    // ラベル
    p.noStroke();
    p.fill(color[0], color[1], color[2], stock > 0 ? 128 : 64);
    p.textAlign(p.CENTER, p.CENTER);
    p.textSize(8);
    p.text(label, x, y + 4);

    // ドットゲージ（横並び）
    const dotSize = 10;
    const dotGap = 6;
    const dotY = y + 26;
    const totalDotsWidth = maxStock * dotSize + (maxStock - 1) * dotGap;
    const startDotX = x - totalDotsWidth / 2 + dotSize / 2;

    for (let i = 0; i < maxStock; i++) {
        const dotX = startDotX + i * (dotSize + dotGap);
        const isFilledDot = i < stock;

        if (isFilledDot) {
            // アクティブドット（発光）
            const dotPulse = isActive ? 0.7 + p.sin(p.frameCount * 0.15 + i * 0.3) * 0.3 : 1.0;
            // グロー
            p.noStroke();
            p.fill(color[0], color[1], color[2], 100 * dotPulse);
            p.rect(dotX - dotSize / 2 - 2, dotY - dotSize / 2 - 2, dotSize + 4, dotSize + 4, 4);
            // ドット本体
            p.fill(color[0], color[1], color[2]);
            p.stroke(color[0], color[1], color[2]);
            p.strokeWeight(1);
            p.rect(dotX - dotSize / 2, dotY - dotSize / 2, dotSize, dotSize, 2);
        } else {
            // 非アクティブドット
            p.fill(color[0], color[1], color[2], 38);
            p.stroke(color[0], color[1], color[2], 77);
            p.strokeWeight(1);
            p.rect(dotX - dotSize / 2, dotY - dotSize / 2, dotSize, dotSize, 2);
        }
    }
    p.noStroke();
}

/**
 * ボタンアイコンを描画
 */
function drawButtonIcon(
    p: ReturnType<typeof getP5>,
    x: number,
    y: number,
    icon: 'bomb' | 'homing' | 'cannon',
    color: readonly [number, number, number],
    hasStock: boolean,
    isActive: boolean
): void {
    const iconColor = hasStock ? color : [80, 80, 80] as const;
    const pulse = isActive ? 0.8 + p.sin(p.frameCount * 0.15) * 0.2 : 1.0;

    p.noStroke();
    p.fill(iconColor[0], iconColor[1], iconColor[2], 255 * pulse);

    if (icon === 'bomb') {
        // 爆撃弾頭形状（ノーズ上向き、尾翼が下）
        p.beginShape();
        p.vertex(x, y - 10);        // ノーズ先端（上）
        p.vertex(x + 3, y - 5);     // 右テーパー
        p.vertex(x + 5, y + 2);     // 右最大幅
        p.vertex(x + 4, y + 8);     // 右下
        p.vertex(x, y + 10);        // 下部中央
        p.vertex(x - 4, y + 8);     // 左下
        p.vertex(x - 5, y + 2);     // 左最大幅
        p.vertex(x - 3, y - 5);     // 左テーパー
        p.endShape(p.CLOSE);
        // 尾翼（下部安定翼）
        p.triangle(x - 7, y + 6, x - 3, y + 10, x - 3, y + 4);
        p.triangle(x + 7, y + 6, x + 3, y + 10, x + 3, y + 4);
    } else if (icon === 'homing') {
        // 実弾ミサイル形状
        p.push();
        p.translate(x, y);

        // ブースター炎（アクティブ時は揺らぎ）
        const flicker = hasStock && isActive ? 0.8 + p.sin(p.frameCount * 0.4) * 0.4 : 1.0;
        const flameLen = 8 * flicker;
        p.noStroke();
        // 外炎（オレンジ）
        p.fill(255, 80, 0, hasStock ? 200 : 80);
        p.beginShape();
        p.vertex(-3, 7);
        p.vertex(0, 7 + flameLen);
        p.vertex(3, 7);
        p.endShape(p.CLOSE);
        // 中炎（黄色）
        p.fill(255, 180, 50, hasStock ? 230 : 100);
        p.beginShape();
        p.vertex(-2, 7);
        p.vertex(0, 7 + flameLen * 0.7);
        p.vertex(2, 7);
        p.endShape(p.CLOSE);

        // 本体（先端 + 胴体）
        p.fill(iconColor[0], iconColor[1], iconColor[2], 255 * pulse);
        p.stroke(255, 255, 150, hasStock ? 200 : 80);
        p.strokeWeight(1);
        // 先端（三角形）
        p.beginShape();
        p.vertex(0, -10);
        p.vertex(-3, -4);
        p.vertex(3, -4);
        p.endShape(p.CLOSE);
        // 胴体
        p.rect(-3, -4, 6, 11, 1);

        // 尾翼
        p.fill(220, 160, 40, hasStock ? 255 : 100);
        p.noStroke();
        p.beginShape();
        p.vertex(-3, 3);
        p.vertex(-6, 8);
        p.vertex(-3, 7);
        p.endShape(p.CLOSE);
        p.beginShape();
        p.vertex(3, 3);
        p.vertex(6, 8);
        p.vertex(3, 7);
        p.endShape(p.CLOSE);

        // 中心ライン
        p.fill(255, 255, 255, hasStock ? 255 : 80);
        p.noStroke();
        p.rect(-0.8, -7, 1.6, 8, 1);

        p.pop();
    } else if (icon === 'cannon') {
        // 荷電粒子砲ビーム（砲口が狭く上方に広がる）
        p.push();
        p.translate(x, y);
        p.noStroke();

        // 外側ビーム（紫グロー）
        p.fill(iconColor[0], iconColor[1], iconColor[2], 80 * pulse);
        p.beginShape();
        p.vertex(-7, -12);
        p.vertex(7, -12);
        p.vertex(2, 6);
        p.vertex(-2, 6);
        p.endShape(p.CLOSE);

        // 内側ビーム（白コア）
        p.fill(255, 255, 255, hasStock ? 180 * pulse : 60);
        p.beginShape();
        p.vertex(-3, -12);
        p.vertex(3, -12);
        p.vertex(0.8, 6);
        p.vertex(-0.8, 6);
        p.endShape(p.CLOSE);

        // 砲口
        p.fill(iconColor[0], iconColor[1], iconColor[2], 255 * pulse);
        p.ellipse(0, 8, 10, 5);

        // アクティブ時: 砲口フラッシュ
        if (hasStock && isActive) {
            const glowSize = 4 + p.sin(p.frameCount * 0.3) * 2;
            p.fill(255, 255, 255, 180);
            p.ellipse(0, 8, glowSize, glowSize);
        }

        p.pop();
    }
}
